import React from 'react';
import * as Sentry from '@sentry/nextjs';
import { Paper, Grid, Typography, InputAdornment, Alert } from '@mui/material';
import { Control } from 'react-hook-form';
import { QuestionType, QUESTION_TYPE } from '@/types/enums/dynamicProfiling/questionType';
import { IDynamicProfilingQuestion, IDynamicProfilingTranslations } from '@/types/types';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { DYNAMIC_PROFILING_GENERAL_PHRASES } from '@/consts/phrases/dynamicProfilingPhrases';
import { isArrayWithValues } from '@/utils/fp';
import { JSXFromString } from '@/utils';
import { AutoCompleteField } from '@/components/forms/inputs/AutoCompleteField/AutoCompleteField';
import { RadioField } from '@/components/forms/inputs/RadioField/RadioField';
import { CheckboxField } from '@/components/forms/inputs/CheckBoxField/CheckboxField';
import { TextField } from '@/components/forms/inputs/TextField/TextField';

export interface DynamicProfilingQuestionProps {
    data: IDynamicProfilingQuestion;
    control: Control;
    translations: IDynamicProfilingTranslations;
}

export const DynamicProfilingQuestion = ({
    control,
    data,
    translations,
}: DynamicProfilingQuestionProps) => {
    const {
        question: {
            questionId,
            descriptionPhraseId,
            questionTypeId,
            isAutoComplete,
            phraseId,
            min: minValue,
            max: maxValue,
            symbolPhraseId,
        },
        answers,
        error,
    } = data;
    const { t } = usePhrase();
    const currentType =
        QuestionType[questionTypeId as (typeof QUESTION_TYPE)[keyof typeof QUESTION_TYPE]];

    const sendDynamicProfilingQuestionErrorToSentry = (
        message: string,
        questionId: number,
        phraseId?: number,
        answerId?: number,
    ) => {
        Sentry.captureException(new Error(message), {
            extra: {
                questionId,
                phraseId,
                answerId,
            },
        });
    };

    const checkForError = (questionId: number, phraseId?: number, answerId?: number) => {
        if (!phraseId || !answerId) {
            sendDynamicProfilingQuestionErrorToSentry(
                'Corrupted answer data in Dynamic Profiling Response',
                questionId,
                phraseId,
                answerId,
            );
            return true;
        }
        if (phraseId && !translations[phraseId]) {
            sendDynamicProfilingQuestionErrorToSentry(
                'Missing Translation for PhraseId in Dynamic Profiling Response',
                questionId,
                phraseId,
            );
            return true;
        }
        return false;
    };

    const getOptions = (questionId: number, answers: IDynamicProfilingQuestion['answers']) => {
        const options: { label: string; value: string }[] = [];
        answers.map(({ phraseId, answerId }) => {
            if (!checkForError(questionId, phraseId, answerId)) {
                options.push({
                    label: translations[phraseId],
                    value: String(answerId),
                });
            }
        });
        return options;
    };

    return (
        <Paper sx={{ p: 4, mt: 2, mb: 2 }} className="ProfileQuestionsContent">
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant="h5" className="ProfileQuestionTitle" sx={{ mb: 1 }}>
                        {translations[phraseId]}
                    </Typography>
                    {descriptionPhraseId && (
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {JSXFromString(translations[descriptionPhraseId])}
                        </Typography>
                    )}
                </Grid>
                {error && error?.phraseId && (
                    <Grid size={12} sx={{ mb: 1, pb: 1 }}>
                        <Alert severity="error" id="message_box">
                            {t(error.phraseId, { replaceTextVars: error?.vars })}
                        </Alert>
                    </Grid>
                )}
                {isArrayWithValues(answers) && (
                    <>
                        {currentType === QuestionType[QUESTION_TYPE.SINGLE_PICK] && (
                            <>
                                {answers.length > 10 || isAutoComplete ? (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <AutoCompleteField
                                            control={control}
                                            name={String(questionId)}
                                            label={DYNAMIC_PROFILING_GENERAL_PHRASES.PLEASE_SELECT}
                                            data={getOptions(questionId, answers)}
                                            fullWidth
                                        />
                                    </Grid>
                                ) : (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <RadioField
                                            control={control}
                                            name={String(questionId)}
                                            fullWidth
                                            data={getOptions(questionId, answers)}
                                        />
                                    </Grid>
                                )}
                            </>
                        )}
                        {currentType === QuestionType[QUESTION_TYPE.MULTI_PICK] && (
                            <>
                                {isAutoComplete ? (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <AutoCompleteField
                                            control={control}
                                            name={String(questionId)}
                                            label={DYNAMIC_PROFILING_GENERAL_PHRASES.PLEASE_SELECT}
                                            multiple
                                            data={getOptions(questionId, answers)}
                                            fullWidth
                                        />
                                    </Grid>
                                ) : (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        {answers.map(({ phraseId, answerId }) => {
                                            if (!checkForError(questionId, phraseId, answerId)) {
                                                return (
                                                    <div key={answerId}>
                                                        <CheckboxField
                                                            control={control}
                                                            key={answerId}
                                                            name={`${questionId}-${answerId}`}
                                                            label={translations[phraseId]}
                                                            fullWidth
                                                        />
                                                    </div>
                                                );
                                            }
                                        })}
                                    </Grid>
                                )}
                            </>
                        )}
                        {currentType === QuestionType[QUESTION_TYPE.NUMERIC_GRID] && (
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Grid container spacing={2}>
                                    {answers.map(({ phraseId, answerId }) => {
                                        if (!checkForError(questionId, phraseId, answerId)) {
                                            return (
                                                <Grid size={12} key={answerId}>
                                                    <TextField
                                                        control={control}
                                                        name={`${questionId}-${answerId}`}
                                                        label={translations[phraseId]}
                                                        error={undefined}
                                                        fullWidth
                                                        slotProps={{
                                                            input: {
                                                                endAdornment: symbolPhraseId ? (
                                                                    <InputAdornment position="end">
                                                                        <Typography variant="caption">
                                                                            {t(symbolPhraseId)}
                                                                        </Typography>
                                                                    </InputAdornment>
                                                                ) : null,
                                                                type: 'number',
                                                            },
                                                            htmlInput: {
                                                                min: minValue,
                                                                max: maxValue,
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                            );
                                        }
                                    })}
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </Paper>
    );
};
